import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './AdminPaymentDates.css';

const thaiMonthsFull = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
                        'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
const thaiMonthsShort = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
                         'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// แปลง emp_types CSV ให้เป็น key มาตรฐาน (เรียงตัวเลขจากน้อยไปมาก)
function normalizeEmpTypesKey(csv) {
    return String(csv || '')
        .split(',')
        .map(s => parseInt(s, 10))
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b)
        .join(',');
}

function AdminPaymentDates() {
    const currentBudgetYear = new Date().getFullYear() + 543 + (new Date().getMonth() >= 9 ? 1 : 0);

    const [empTypes, setEmpTypes] = useState([]);
    const [paymentDates, setPaymentDates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(currentBudgetYear);

    // Modal state
    const [showForm, setShowForm] = useState(false);
    const [editingKey, setEditingKey] = useState(null); // key = normalized emp_types CSV
    const [formData, setFormData] = useState({
        emp_types: [],            // number[]
        dates: {},                // { [month]: 'YYYY-MM-DD' }
        description: ''
    });
    const [saving, setSaving] = useState(false);

    const fetchEmpTypes = useCallback(async () => {
        try {
            const res = await api.get('/admin/emp-types');
            setEmpTypes(res.data);
        } catch (err) {
            console.error('Error fetching emp types:', err);
        }
    }, []);

    const fetchPaymentDates = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/employee-payment-dates', {
                params: { year: selectedYear }
            });
            setPaymentDates(res.data);
        } catch (err) {
            console.error('Error fetching payment dates:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedYear]);

    useEffect(() => { fetchEmpTypes(); }, [fetchEmpTypes]);
    useEffect(() => { fetchPaymentDates(); }, [fetchPaymentDates]);

    // จัดกลุ่ม row → ตาราง group: { 'emp_types_key': { empTypes: [..], months: { 1: row, 2: row, ... } } }
    const groups = paymentDates.reduce((acc, row) => {
        const key = normalizeEmpTypesKey(row.emp_types);
        if (!acc[key]) {
            acc[key] = {
                key,
                empTypes: key.split(',').map(Number),
                months: {}
            };
        }
        acc[key].months[row.month] = row;
        return acc;
    }, {});
    const groupList = Object.values(groups).sort((a, b) => a.key.localeCompare(b.key));

    const getEmpTypeMeta = (typeId) => {
        return empTypes.find(e => e.emp_type === typeId);
    };

    const getEmpTypeNames = (typeIds) => {
        return typeIds.map(t => {
            const m = getEmpTypeMeta(t);
            return m ? (m.short_name || m.emp_name) : `Type ${t}`;
        }).join(', ');
    };

    const formatDateShort = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return `${d.getDate()} ${thaiMonthsShort[d.getMonth() + 1]} ${d.getFullYear() + 543}`;
    };

    const openCreateForm = () => {
        setEditingKey(null);
        setFormData({
            emp_types: [],
            dates: {},
            description: ''
        });
        setShowForm(true);
    };

    const openEditForm = (group) => {
        setEditingKey(group.key);
        // เติม dates จาก rows ที่มีอยู่
        const dates = {};
        ALL_MONTHS.forEach(m => {
            const row = group.months[m];
            if (row?.payment_date) {
                dates[m] = String(row.payment_date).split('T')[0];
            }
        });
        // เอา description จากเดือนแรกที่มี (ถ้ามี)
        const firstRow = ALL_MONTHS.map(m => group.months[m]).find(Boolean);
        setFormData({
            emp_types: [...group.empTypes],
            dates,
            description: firstRow?.description || ''
        });
        setShowForm(true);
    };

    const toggleEmpType = (typeId) => {
        if (editingKey) return; // ห้ามแก้ emp_types ตอน edit
        setFormData(prev => ({
            ...prev,
            emp_types: prev.emp_types.includes(typeId)
                ? prev.emp_types.filter(t => t !== typeId)
                : [...prev.emp_types, typeId]
        }));
    };

    const updateMonthDate = (month, value) => {
        setFormData(prev => ({
            ...prev,
            dates: { ...prev.dates, [month]: value }
        }));
    };

    const clearMonthDate = (month) => {
        setFormData(prev => {
            const next = { ...prev.dates };
            delete next[month];
            return { ...prev, dates: next };
        });
    };

    // กรอกอัตโนมัติ: ใช้วันเดียวกันทุกเดือนของปี (เช่น ทุกวันที่ 27)
    const fillSameDayAllMonths = () => {
        const refDate = Object.values(formData.dates).find(Boolean);
        let dayOfMonth = 27;
        if (refDate) {
            dayOfMonth = parseInt(refDate.split('-')[2], 10) || 27;
        } else {
            const input = window.prompt('กรอกวันที่ของเดือน (1-31):', '27');
            if (!input) return;
            dayOfMonth = parseInt(input, 10);
            if (isNaN(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) return;
        }
        // budget year = พ.ศ. — แปลงเป็น ค.ศ.
        const ceYear = selectedYear - 543;
        const next = {};
        ALL_MONTHS.forEach(m => {
            // budget year มกราคม = ปีเดียวกัน — ปีงบไทย ต.ค. = เริ่มต้น
            // ตัวอย่าง: ปีงบ 2569 = ต.ค.2568 – ก.ย.2569
            // เพื่อความง่าย: เดือน 10-12 ใช้ปี (ceYear-1), เดือน 1-9 ใช้ปี ceYear
            const yearForMonth = (m >= 10) ? (ceYear - 1) : ceYear;
            // clamp day to last day of month
            const daysInMonth = new Date(yearForMonth, m, 0).getDate();
            const actualDay = Math.min(dayOfMonth, daysInMonth);
            const mm = String(m).padStart(2, '0');
            const dd = String(actualDay).padStart(2, '0');
            next[m] = `${yearForMonth}-${mm}-${dd}`;
        });
        setFormData(prev => ({ ...prev, dates: next }));
    };

    const handleSave = async () => {
        if (formData.emp_types.length === 0) {
            alert('กรุณาเลือกประเภทพนักงานอย่างน้อย 1 ประเภท');
            return;
        }
        const datesArr = ALL_MONTHS
            .filter(m => formData.dates[m])
            .map(m => ({
                month: m,
                payment_date: formData.dates[m],
                description: formData.description || ''
            }));
        if (datesArr.length === 0) {
            alert('กรุณากรอกวันที่อย่างน้อย 1 เดือน');
            return;
        }

        setSaving(true);
        try {
            await api.post('/admin/employee-payment-dates/group', {
                budget_year: selectedYear,
                emp_types: formData.emp_types.join(','),
                dates: datesArr
            });
            setShowForm(false);
            setEditingKey(null);
            await fetchPaymentDates();
        } catch (err) {
            alert(err.response?.data?.error || 'เกิดข้อผิดพลาด');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteGroup = async (group) => {
        const names = getEmpTypeNames(group.empTypes);
        if (!window.confirm(`ลบทั้งกลุ่ม "${names}" ของปี ${selectedYear} หรือไม่?\nรายการทั้งหมด ${Object.keys(group.months).length} เดือนจะถูกลบ`)) {
            return;
        }
        try {
            await api.delete('/admin/employee-payment-dates/group', {
                params: { budget_year: selectedYear, emp_types: group.key }
            });
            await fetchPaymentDates();
        } catch (err) {
            alert(err.response?.data?.error || 'เกิดข้อผิดพลาด');
        }
    };

    // emp_types ที่เลือกได้ (กรองคนที่ has_fixed_date ออก เช่น ข้าราชการ/พนักงานราชการ)
    const selectableTypes = empTypes.filter(t => !t.has_fixed_date);

    return (
        <div className="admin-payment-dates">
            <div className="page-header">
                <div className="header-content">
                    <h1>กำหนดวันโอนเงินเดือนตามกลุ่ม</h1>
                    <p>จัดกลุ่มพนักงานที่ใช้วันโอนเดียวกัน แล้วกรอกวันที่ของ 12 เดือนพร้อมกันในที่เดียว</p>
                </div>
                <button className="btn-add" onClick={openCreateForm}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    เพิ่มกลุ่มใหม่
                </button>
            </div>

            {/* Year Selector */}
            <div className="year-selector">
                <label>ปีงบประมาณ:</label>
                <div className="year-tabs">
                    {[currentBudgetYear - 1, currentBudgetYear, currentBudgetYear + 1].map(year => (
                        <button
                            key={year}
                            className={`year-tab ${selectedYear === year ? 'active' : ''}`}
                            onClick={() => setSelectedYear(year)}
                        >
                            {year}
                        </button>
                    ))}
                </div>
            </div>

            {/* Info */}
            <div className="info-card">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
                <div>
                    <strong>หมายเหตุ:</strong> ข้าราชการ + พนักงานราชการ + ลูกจ้างประจำ ใช้วันโอนเดียวกัน
                    ตามประกาศกรมบัญชีกลาง (ตั้งในเมนู "วันเงินเดือนหลัก") — หน้านี้ใช้สำหรับ
                    <strong> ลูกจ้างประเภทอื่นที่โอนคนละวัน</strong> เช่น พกส., ลูกจ้างชั่วคราว
                </div>
            </div>

            {/* Groups List */}
            {loading ? (
                <div className="loading">
                    <div className="spinner"></div>
                    <p>กำลังโหลดข้อมูล...</p>
                </div>
            ) : groupList.length === 0 ? (
                <div className="empty-state">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="64" height="64">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    <p>ยังไม่มีกลุ่มที่กำหนดสำหรับปี {selectedYear}</p>
                    <button className="btn-add-empty" onClick={openCreateForm}>เพิ่มกลุ่ม</button>
                </div>
            ) : (
                <div className="group-list">
                    {groupList.map(group => (
                        <div key={group.key} className="group-card">
                            <div className="group-card-header">
                                <div className="group-title">
                                    <span className="group-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                            <circle cx="9" cy="7" r="4"/>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                        </svg>
                                    </span>
                                    <div>
                                        <h3>{getEmpTypeNames(group.empTypes)}</h3>
                                        <span className="group-meta">{group.empTypes.length} ประเภท · ปี {selectedYear}</span>
                                    </div>
                                </div>
                                <div className="group-actions">
                                    <button className="btn-icon edit" onClick={() => openEditForm(group)} title="แก้ไขทั้งกลุ่ม">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="18" height="18">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                        </svg>
                                    </button>
                                    <button className="btn-icon delete" onClick={() => handleDeleteGroup(group)} title="ลบทั้งกลุ่ม">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="18" height="18">
                                            <path d="M3 6h18"/>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="group-card-body">
                                <table className="months-table">
                                    <thead>
                                        <tr>
                                            <th>เดือน</th>
                                            <th>วันโอน</th>
                                            <th>หมายเหตุ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ALL_MONTHS.map(m => {
                                            const row = group.months[m];
                                            return (
                                                <tr key={m} className={row ? '' : 'empty'}>
                                                    <td className="col-month">{thaiMonthsFull[m]}</td>
                                                    <td className="col-date">
                                                        {row ? formatDateShort(row.payment_date) : <span className="muted">ยังไม่กำหนด</span>}
                                                    </td>
                                                    <td className="col-note">
                                                        {row?.description || <span className="muted">—</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit Group Modal */}
            {showForm && (
                <div className="modal-overlay" onClick={() => !saving && setShowForm(false)}>
                    <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingKey ? 'แก้ไขกลุ่ม' : 'เพิ่มกลุ่มใหม่'}</h3>
                            <button className="modal-close" onClick={() => setShowForm(false)} disabled={saving}>&times;</button>
                        </div>

                        <div className="modal-body">
                            {/* ปีงบประมาณ (read-only) */}
                            <div className="form-section">
                                <label className="form-label">ปีงบประมาณ</label>
                                <div className="readonly-value">{selectedYear}</div>
                            </div>

                            {/* เลือกประเภทพนักงาน */}
                            <div className="form-section">
                                <label className="form-label">
                                    ประเภทพนักงานในกลุ่มนี้
                                    {editingKey && <span className="hint"> (ไม่สามารถแก้ตอนแก้ไขกลุ่ม — ลบกลุ่มเดิมเพื่อสร้างใหม่)</span>}
                                </label>
                                <div className="emp-type-grid">
                                    {selectableTypes.map(type => (
                                        <label key={type.emp_type} className={`emp-type-checkbox ${editingKey ? 'disabled' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={formData.emp_types.includes(type.emp_type)}
                                                onChange={() => toggleEmpType(type.emp_type)}
                                                disabled={editingKey}
                                            />
                                            <span className="checkmark"></span>
                                            <span className="type-name">{type.emp_name}</span>
                                            <span className="type-short">({type.short_name})</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* ตาราง 12 เดือน */}
                            <div className="form-section">
                                <div className="month-grid-header">
                                    <label className="form-label">วันโอนเงินรายเดือน</label>
                                    <button type="button" className="btn-quick-fill" onClick={fillSameDayAllMonths}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                                            <polyline points="9 11 12 14 22 4"/>
                                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                                        </svg>
                                        เติมวันเดียวกันทุกเดือน
                                    </button>
                                </div>
                                <div className="month-input-grid">
                                    {ALL_MONTHS.map(m => (
                                        <div key={m} className="month-input">
                                            <div className="month-input-label">{thaiMonthsFull[m]}</div>
                                            <div className="month-input-row">
                                                <input
                                                    type="date"
                                                    value={formData.dates[m] || ''}
                                                    onChange={e => updateMonthDate(m, e.target.value)}
                                                />
                                                {formData.dates[m] && (
                                                    <button
                                                        type="button"
                                                        className="btn-clear"
                                                        onClick={() => clearMonthDate(m)}
                                                        title="ล้างเดือนนี้"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="month-hint">
                                    เว้นเดือนใดไว้ = ไม่กำหนดวันโอนของเดือนนั้น (ระบบจะใช้ค่าจากวันเงินเดือนหลักแทน)
                                </div>
                            </div>

                            {/* หมายเหตุ */}
                            <div className="form-section">
                                <label className="form-label">หมายเหตุ (ใช้กับทุกเดือนในกลุ่ม)</label>
                                <input
                                    type="text"
                                    className="form-text"
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="เช่น โอนล่วงหน้าก่อนวันหยุด"
                                />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-cancel" onClick={() => setShowForm(false)} disabled={saving}>
                                ยกเลิก
                            </button>
                            <button type="button" className="btn-submit" onClick={handleSave} disabled={saving}>
                                {saving ? 'กำลังบันทึก...' : (editingKey ? 'บันทึกการแก้ไข' : 'เพิ่มกลุ่ม')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminPaymentDates;
